    <script setup lang="ts">
    import { ref, onMounted, watch, computed } from 'vue';
    import type { AnyThing } from "@/ProductTypes";

    const props = withDefaults(defineProps<{
      slides: AnyThing[]
      displayMode?: 'landscape'|'portrait'
    }>(), {
      displayMode: 'landscape'
    })
    
    /**
     * CAROUSEL STATE MANAGEMENT
     * This interface tracks the state of the carousel including card elements, scroll position, and display settings
     */
    interface CarouselState {
      cards: HTMLElement[];         // References to card DOM elements
      scrollLeft: number;           // Current scroll position (for keyboard focus)
      isRTL: boolean;               // Right-to-left text direction support
      isSingleSlide: boolean;       // Whether to show only one slide at a time
    }
    
    

    // Computed properties for card ratio and image dimensions
    const ratioClass = computed(() => {
        // Return classes based on display mode
      // TWEAK: Add more display modes by adding cases to the switch statement
      switch (props.displayMode) {
        case 'portrait':
          return 'Card--square'; // 1:1 ratio
        case 'landscape':
          return 'Card--wide'; // 16:9 ratio
        default:
          return 'Card--square';
      }
    });
    // Calculate image dimensions based on display mode
    const imageWidth = computed(() => {
      return props.displayMode === 'portrait' ? 480 : 720; // Square vs wide
    });
    
    const imageHeight = computed(() => {
      return props.displayMode === 'portrait' ? 480 : 480; // Same height in both cases
    });
    
    // Detect if user prefers reduced motion for accessibility
    const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    /**
     * DOM REFERENCES
     * Refs to access and manipulate DOM elements
     */
    const carouselRef = ref<HTMLElement | null>(null);        // Main carousel container
    const paginationRef = ref<HTMLElement | null>(null);      // Pagination dots container
    const previousButtonRef = ref<HTMLButtonElement | null>(null); // Previous button
    const nextButtonRef = ref<HTMLButtonElement | null>(null);     // Next button
    const startDivRef = ref<HTMLElement | null>(null);        // Start placeholder for observer
    const endDivRef = ref<HTMLElement | null>(null);          // End placeholder for observer
    const activeCardIndex = ref(props.slides.length > 1 ? 1 : 0);// start on the 2nd slide if there are at least 2 slides
    
    
    // State management
    const carouselState = ref<CarouselState>({
      cards: [],
      scrollLeft: 0,
      isRTL: document.dir === 'rtl',
      isSingleSlide: false
    });
    
    // Get card position style for 3D stacking effect
    /**
     * This function calculates the position styles for each card based on its position
     * relative to the active card, creating the 3D stacked effect
     * 
     * CUSTOMIZATION: Adjust parameters here to change the visual appearance of the stack
     * 
     * @param index - The index of the card in the visible slides array
     * @returns CSS styles object for positioning the card in 3D space
     */
        const getCardPositionStyle = (index: number) => {
      const centerIndex = activeCardIndex.value;
      const distance = index - centerIndex;
      const isActive = distance === 0;
      
      // Z-index decreases as distance from center increases
      const zIndex = 100 - Math.abs(distance) * 10;
      
      // Scale cards based on distance from center (further = smaller)
      // TWEAK: Adjust the 0.09 value to change how much cards shrink with distance
      const scale = isActive ? 1 : Math.max(0.9, 1 - (Math.abs(distance) * 0.09));
      
      // Horizontal positioning - cards spread out from center
      // TWEAK: Adjust the 65% value to change how far apart cards are positioned
      const translateX = distance === 0 ? '0' : `${distance * 65}%`;
      
      // Depth positioning - cards further from center move back in Z space
      // TWEAK: Adjust the 60px value to change the depth effect
      const translateZ = isActive ? '0px' : `-${Math.abs(distance) * 60}px`;
      
      // Visual effects to enhance the 3D appearance
      // TWEAK: Adjust these values to change the visual treatment of non-active cards
      const brightness = isActive ? '100%' : `${100 - Math.abs(distance) * 12}%`;
      const opacity = isActive ? 1 : Math.max(1, 1 - (Math.abs(distance) * 0.15));
      const border = isActive ? '3px solid rgba(34, 204, 238, 0.7)' : '1px solid rgba(45, 210, 207, 0.2)';
      const boldText = isActive ? 'bold' : 'normal';
      //       // Reduced opacity values in the box shadow for a much more subtle effect
      // const boxShadow = isActive 
      //   ? '0 0 45px rgba(34, 204, 238, 0.1), 0 0 5px rgba(34, 204, 238, 0.15), 0 0 55px rgba(34, 204, 238, 0.08), 0 0 55px rgba(34, 204, 238, 0.05)'
      //   : 'var(--cyber-glow)';
      // Return complete style object for the card
      return {
        zIndex: zIndex,
        transform: `translateX(${translateX}) translateZ(${translateZ}) scale(${scale})`,
        filter: `brightness(${brightness})`,
        opacity: opacity,
        // boxShadow: boxShadow,
        border: border,
        fontWeight: boldText,
      };
    };
    
    
    /**
     * CSS HELPER FUNCTIONS
     * Utility for dynamically setting inline CSS properties
     */
    // const setStyleProps = (el: HTMLElement, styles: Record<string, string | boolean>) => {
    //   for (const [key, value] of Object.entries(styles)) {
    //     if (value === false) {
    //       el.style.removeProperty(key);
    //     } else {
    //       el.style.setProperty(key, String(value));
    //     }
    //   }
    // };
    
    // Scroll handling
    let scrollTimeout: number | null = null;
    
    // Get scroll offset for an element
    const getOffset = (el: HTMLElement): number => {
      if (!carouselRef.value) return 0;
      
      let offset = el.offsetLeft;
      if (carouselState.value.isRTL) {
        offset = carouselRef.value.offsetWidth - (offset + el.offsetWidth);
      }
      return offset;
    };
    
    // Scroll to a specific card
    const scrollTo = (card: HTMLElement): void => {
      if (!carouselRef.value) return;
      
      const offset = getOffset(card);
      const left = carouselState.value.isRTL ? -offset : offset;
      const behavior = isReducedMotion ? 'auto' : 'smooth';
      carouselRef.value.scrollTo({ left, behavior });
    };
    /**
     * NAVIGATION HANDLERS
     * Handle user interactions for navigating the carousel
     */
    // Navigate to previous slide
    const handlePreviousClick = (event: Event): void => {
      event.preventDefault();
      if (activeCardIndex.value > 0) {
        activeCardIndex.value--;
      } else if (props.slides.length > 0) {
        activeCardIndex.value = props.slides.length - 1;
      }
    };
    
    // Handle next button click
    const handleNextClick = (event: Event): void => {
      event.preventDefault();
      if (activeCardIndex.value < props.slides.length - 1) {
        activeCardIndex.value++;
      } else {
        // Loop to the beginning if at the end
        activeCardIndex.value = 0;
      }
    };
    
    // Handle dot click in pagination
    const handleDotClick = (event: Event): void => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('Dot')) {
        event.preventDefault();
        const href = (target as HTMLAnchorElement).getAttribute('href');
        if (href) {
          // Extract the slide index from the href
          const cardId = href.replace('#card-', '');
          const index = parseInt(cardId) - 1;
          if (!isNaN(index) && index >= 0 && index < props.slides.length) {
            activeCardIndex.value = index;
          }
        }
      }
    };
    
    // Handle scroll event
    const handleScroll = (): void => {
      if (!carouselRef.value || !paginationRef.value) return;
      
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = window.setTimeout(() => {
        // Update dot indicators
        const dots = paginationRef.value?.querySelectorAll('.Dot');
        const activeDot = paginationRef.value?.querySelector('.Dot--active');
        if (activeDot) activeDot.classList.remove('Dot--active');
        
        if (dots && dots[activeCardIndex.value]) {
          dots[activeCardIndex.value].classList.add('Dot--active');
        }
      }, 50);
    };
    
    // Handle keydown for arrow navigation
    const handleKeydown = (event: KeyboardEvent): void => {
      if (/^(Arrow)?Left$/.test(event.key)) {
        event.preventDefault();
        handlePreviousClick(event);
      } else if (/(Arrow)?Right$/.test(event.key)) {
        event.preventDefault();
        handleNextClick(event);
      }
    };
    
    // Handle blur for tracking scroll position
    const handleBlur = (event: FocusEvent): void => {
      if (!carouselRef.value) return;
      carouselState.value.scrollLeft = carouselRef.value.scrollLeft;
    };
    
    // Handle focus for smooth transitions
    const handleFocus = (event: FocusEvent): void => {
      if (!carouselRef.value) return;
      carouselRef.value.scrollLeft = carouselState.value.scrollLeft;
    };
    
    // Initialize everything on mount
    onMounted(async () => {
      if (!carouselRef.value) return;
      
      // Reset scroll position
      carouselRef.value.scrollLeft = 0;
      
      // Create placeholder elements
      const startDiv = document.createElement('div');
      const endDiv = document.createElement('div');
      carouselRef.value.prepend(startDiv);
      carouselRef.value.append(endDiv);
      
      startDivRef.value = startDiv;
      endDivRef.value = endDiv;
      
      // Get all cards
      carouselState.value.cards = Array.from(
      carouselRef.value.querySelectorAll('.Card')
      ) as HTMLElement[];

      // scroll to the "active" card
      if (carouselState.value.cards[activeCardIndex.value]) {
      scrollTo(carouselState.value.cards[activeCardIndex.value]);
      }

      // Initialize dot
       if (paginationRef.value) {
         const dots = paginationRef.value.querySelectorAll('.Dot');
         dots.forEach((dot, i) => {
           dot.classList.toggle('Dot--active', i === activeCardIndex.value);
         });
       }
    });
    
    // Watch for active card changes
    watch(() => activeCardIndex.value, () => {
      handleScroll();
    });

    // This watcher is to reset the activeCardIndex when slides array changes (if current idx is bigger than new carousel length)
    watch(() => props.slides, (newSlides) => {
      // If activeCardIndex is now invalid (beyond array bounds)
      if (activeCardIndex.value >= newSlides.length && newSlides.length > 0) {
        // Reset to the first slide
        activeCardIndex.value = 0;
        
        // Make sure pagination dots update
        nextTick(() => {
          handleScroll();
        });
      }
    }, { immediate: true, deep: true });


   // imageUrl.value = await getImage('PWACard_vue_1');

    </script>



<template>
    <!-- Main container - holds carousel elements -->
  <div class="carousel-container">
    <!-- Carousel section - sliding component with focus/keyboard support -->
    <section 
    id="carousel" ref="carouselRef" tabindex="-1" class="Carousel" 
      @scroll="handleScroll" 
      @keydown="handleKeydown"
      @blur.capture="handleBlur"
      @focus.capture="handleFocus">
      <h2 class="Hidden" aria-labelledby="Products List">Carousel</h2>
      
      <!-- Dynamic cards -->
      <article 
        v-for="(slide, index) in slides" 
        :id="`card-${index + 1}`"
        :key="slide.id"
        class="Card Card--overlay" 
        :class="[ratioClass, 'cyber-card']"
        :style="getCardPositionStyle(index)">  <!-- Dynamic 3D positioning -->
         <!-- Card content data -->
        <div class="Card__media">
          <div class="card-glow"></div>
          <img 
            class="Card__image" 
            :alt="slide.alt || `Image ${index + 1}`" 
            :width="imageWidth" 
            :height="imageHeight" 
            loading="lazy" 
            :src="slide.image">
        </div>
        <div class="Card__main">
          <h2 class="Card__heading cyber-heading">{{ slide.name }}</h2>
          
          <!-- tags area at bottom-->
          <RanaTags :tags="slide.tags"/>

          <!--div v-if="slide.tags && slide.tags.length" class="card-tags" >
            <span v-for="(tag, tagIndex) in slide.tags" :key="`tag-${tagIndex}`" class="tag">
              {{ tag }}
            </span>
          </div-->
              
          

        </div>
      </article>
    </section>
        <!-- Navigation controls - dots and arrows -->
    <div class="Pagination cyber-pagination">
      <!-- Previous button -->
      <button ref="previousButtonRef" class="Arrow cyber-arrow" aria-label="Previous slide" @click="handlePreviousClick">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
        </svg>
      </button>
      <!-- Indicator dots -->
      <div  ref="paginationRef" class="Dots" @click="handleDotClick">
        <a
          v-for="(slide, index) in slides"
          :key="`dot-${index}`"
          :href="`#card-${index + 1}`"
          class="Dot cyber-dot"
          :class="{ 'Dot--active': index === activeCardIndex }"
          :aria-label="`Go to slide ${index + 1}`"
        />
      </div>
      <!-- Next button -->
      <button ref="nextButtonRef" class="Arrow cyber-arrow" aria-label="Next slide" @click="handleNextClick" >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
    </div>
  </div>
</template>


<style scoped>
/* Base styles included from your original CSS */
:root {
  --theme-blue: 200, 80%, 38%;
  --theme-red: 350, 63%, 52%;
  --theme-pink: 350, 100%, 69%;
  --theme-black: 0, 7%, 9%;
  --theme-white: 0, 0%, 100%;
  --theme-light: 40, 100%, 97%;
  --theme-dark: 0, 7%, 29%;
  --cyber-teal: 173, 83%, 48%;
  --cyber-blue: 199, 98%, 48%;
  --cyber-glow: 0 0 10px rgba(45, 210, 207, 0.5), 0 0 20px rgba(45, 210, 207, 0.3), 0 0 30px rgba(45, 210, 207, 0.1);
  color: hsl(var(--theme-dark));
}


.Carousel {
  --carousel-gap: 0.5rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 25vh;
  min-height: 200px;
  width: 100%;
  perspective: 1500px;
  z-index: 1;
  overflow: visible;
  scroll-behavior: auto;
  padding: 2rem 0;
}


.cyber-card {
  position: absolute;
  width: 280px; 
  height: auto; /* Let height be determined by content */
  max-height: 22vh; /* Maximum height to avoid overflow */
  border: 1px solid rgba(45, 210, 207, 0.5);
  border-radius: 8px;
  background-color: #051014;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  transform-style: preserve-3d;
  box-shadow: var(--cyber-glow);
  overflow: hidden;
}

.cyber-card .Card__media {
 height: 16vh; /* Make image section take up less space */
  min-height: 120px;
  overflow: hidden;
  position: relative;
  background-color: rgba(10, 30, 40, 0.2);
}

.cyber-card .Card__main {
  height: auto;
  min-height: 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 0.75rem 0.5rem 0.75rem;
  background-color: rgba(5, 20, 30, 0.8);
  border-top: 1px solid rgba(45, 210, 207, 0.5);

}

.cyber-heading {
  font-size: 1rem;
  color: rgba(45, 210, 207, 1);
  text-shadow: 0 0 5px rgba(45, 210, 207, 0.7);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: nowrap; /* Prevent wrapping to new lines */
  overflow-x: auto; /* Enable horizontal scrolling */
  padding-bottom: 0.5rem; 
  max-width: 100%; 
  scrollbar-width: thin; /* Firefox */
  /*scrollbar-color: rgba(45, 210, 207, 0.5) transparent;  Firefox */
  background: radial-gradient(#00ccaa #64F4D5);
  position: relative; 
  -webkit-overflow-scrolling: touch; 
  margin-top: 0.3rem;
}

.tag {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 20px;
  /* background-color: rgba(45, 210, 207, 0.2); */
  border: 1px solid rgba(45, 210, 207, 0.5);
  color: rgba(45, 210, 207, 1);
  font-size: 0.7rem;
  white-space: nowrap;
}

.card-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow: inset 0 0 30px rgba(45, 210, 207, 0.2);
  z-index: 1;
}

/* Pagination styles */
.cyber-pagination {
  margin-top: 2rem;
  position: relative;
  z-index: 2;
}

.cyber-dot {
  border: 1px solid rgba(45, 210, 207, 0.915);
  background-color: transparent;
  transition: all 0.3s ease;
}

.cyber-dot::after {
  background-color: rgba(45, 210, 207, 0.882);
  box-shadow: 0 0 5px rgba(45, 210, 207, 0.7);
}

.cyber-dot.Dot--active {
  background-color: rgb(45, 210, 207);
  box-shadow: 0 0 10px rgba(45, 210, 207, 0.5);
  transform: scale(1.2);
}

.cyber-arrow {
  background-color: rgba(5, 20, 30, 0.842);
  border: 1px solid rgba(45, 210, 207, 0.5);
  color: rgba(45, 210, 207, 1);
  box-shadow: 0 0 10px rgba(45, 210, 207, 0.3);
  transition: all 0.3s ease;
}

.cyber-arrow:hover {
  background-color: rgba(45, 210, 207, 0.2);
  box-shadow: 0 0 15px rgba(45, 210, 207, 0.5);
  color: white;
}

.cyber-arrow svg {
  fill: currentColor;
}

.Card__image {
  object-fit: cover;
  width: 100%;
  height: 100%;
  object-position: center;
}

.Pagination {
  margin: 0.5rem -0.5rem;
}

/* Responsive adjustments */
@media (min-width: 768px) {
  .cyber-card {
    width: 320px;
    max-height: 22vh;
  }
  
  .cyber-heading {
    font-size: 1rem;
  }
}

/* Apply only to mobile phones */
/* @media (min-width: 350px) and (max-width: 767px) {
  .Pagination {
    margin: -2rem -0.5rem;
  }
} */
/* Additional adjustment for narrower phones */
@media (min-width: 320px) and (max-width: 379px) {
  .Pagination {
    margin: -1.5rem -0.25rem; /* Slightly smaller margins */
  }
}
@media (min-width: 1024px) {
  .cyber-card {
    width: 560px;
    max-height: 24vh;
  }
}

/* Ensure the carousel is visible for non-JavaScript browsers */
.js-disabled .Carousel {
  overflow-x: auto;
  scroll-snap-type: x mandatory;
}

.js-disabled .cyber-card {
  position: relative;
  flex: 0 0 auto;
  scroll-snap-align: center;
}

/* Screen animation for cyberspace feel */
@keyframes scanline {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}


.Pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  /* padding: 1rem 0; */
}

.Dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.Dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
}

.Arrow {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.Arrow svg {
  width: 24px;
  height: 24px;
}
</style>